package com.exura.pages.planCreator.components.seriesViews

import androidx.compose.runtime.*
import com.exura.features.plandefinition.domain.builder.series.SimplePersistentSeriesBuilderNew
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.pages.planCreator.components.ViewMode
import com.exura.pages.planCreator.components.seriesViews.inputs.*
import com.exura.pages.planCreator.components.seriesViews.sections.*
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.Dimens
import com.exura.ui.TextStyles
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

/**
 * Enhanced Simple Persistent Series editor view that works with SimplePersistentSeriesBuilderNew
 * Provides real-time editing with change tracking and full progressive disclosure flow
 */
@Composable
internal fun SimplePersistentSeriesView(
    builder: SimplePersistentSeriesBuilderNew,
    isNewSeries: Boolean = true,
    viewMode: ViewMode,
    planType: PlanType,
    onAction: () -> Unit,
) {
    val typeInfo = SeriesTypeRegistry.getTypeInfo(SeriesType.SIMPLE_PERSISTENT)!!
    val inputMode = if (viewMode == ViewMode.EDIT) PlanCreatorInputMode.EDIT else PlanCreatorInputMode.VIEW

    // Default view values - backing state variables
    var name by remember(builder) { mutableStateOf("") }
    var description by remember(builder) { mutableStateOf("") }
    var relativeDay by remember(builder) { mutableStateOf(0) }
    var durationDays by remember(builder) { mutableStateOf(1) }
    var durationInMinutes by remember(builder) { mutableStateOf(60) }
    var startHourOfDay by remember(builder) { mutableStateOf<HourOfDay?>(null) }

    // Mode display values
    var relativeDayMode by remember(builder) { mutableStateOf(RelativeDayMode.ON_VISIT_DAY) }
    var durationMode by remember(builder) { mutableStateOf(DurationMode.DAYS) }
    var relativeDayDisplay by remember(builder) { mutableStateOf(1) }

    // Change tracking
    var hasChanges by remember(builder) { mutableStateOf(false) }
    var isValid by remember(builder) { mutableStateOf(false) }

    // Computed derived state
    val seriesHasName by remember(name) { mutableStateOf(name.isNotBlank()) }
    val hasDescription by remember(description) { mutableStateOf(description.isNotBlank()) }

    fun updateChanges() {
        hasChanges = builder.isChanged
        val validationMessages = builder.validate()
        isValid = validationMessages.isEmpty()
    }

    // Setting on launch/change - initialize from builder state
    LaunchedEffect(builder) {
        name = builder.name() ?: name
        description = builder.description() ?: description
        relativeDay = builder.relativeDay() ?: relativeDay
        durationDays = builder.durationDays() ?: durationDays
        durationInMinutes = builder.durationInMinutes() ?: durationInMinutes
        startHourOfDay = builder.startHourOfDay() ?: startHourOfDay

        // Set mode display values based on stored values
        relativeDayMode = if (relativeDay == 0) RelativeDayMode.ON_VISIT_DAY else RelativeDayMode.DAYS_OFFSET
        durationMode = if (builder.durationInMinutes() != null) DurationMode.MINUTES else DurationMode.DAYS
        relativeDayDisplay = when (planType) {
            PlanType.PREPLAN -> kotlin.math.abs(relativeDay).coerceAtLeast(1)
            PlanType.POSTPLAN -> relativeDay.coerceAtLeast(1)
        }

        // Apply initial values to builder
        builder.setName(name)
        builder.setDescription(description)
        builder.setRelativeDay(relativeDay)
        if (durationMode == DurationMode.DAYS) {
            builder.setDurationDays(durationDays)
            builder.setDurationInMinutes(null)
        } else {
            builder.setDurationInMinutes(durationInMinutes)
            builder.setDurationDays(null)
        }
        builder.setStartHourOfDay(startHourOfDay)

//        builder.markSaved()
    }

    SeriesView(
        hasChanges = hasChanges,
        isValid = isValid,
        typeInfo = typeInfo,
        isNewSeries = isNewSeries,
        viewMode = inputMode,
        onAction = {
            onAction()
            builder.markSaved()
            updateChanges()
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.px)
                .overflow(Overflow.Auto),
            verticalArrangement = Arrangement.spacedBy(0.px)
        ) {

            NameSeriesSection(
                value = name,
                onValueChange = { new ->
                    name = new
                    builder.setName(new)
                    updateChanges()
                },
                mode = inputMode,
            )

            DescriptionSeriesSection(
                value = description,
                onValueChange = { new ->
                    description = new
                    builder.setDescription(new)
                    updateChanges()
                },
                mode = inputMode,
                placeholder = "Opisz ograniczenie lub stan...",
                enabled = seriesHasName,
            )

            RelativeDaySection(
                selectedValue = relativeDayMode,
                relativeDay = relativeDayDisplay,
                onRelativeDayChange = { new ->
                    relativeDayMode = RelativeDayMode.DAYS_OFFSET
                    relativeDayDisplay = new
                    relativeDay = when (planType) {
                        PlanType.PREPLAN -> -new
                        PlanType.POSTPLAN -> new
                    }
                    builder.setRelativeDay(relativeDay)
                    updateChanges()
                },
                onModeChange = { newMode ->
                    relativeDayMode = newMode
                    when(newMode) {
                        RelativeDayMode.NONE -> {}
                        RelativeDayMode.ON_VISIT_DAY -> {
                            builder.setRelativeDay(0)
                            relativeDay = 0
                        }
                        RelativeDayMode.DAYS_OFFSET -> {
                            relativeDay = when (planType) {
                                PlanType.PREPLAN -> -relativeDayDisplay
                                PlanType.POSTPLAN -> relativeDayDisplay
                            }
                            builder.setRelativeDay(relativeDay)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName,
                planType = planType,
            )

            DurationPersistentInput(
                selectedValue = durationMode,
                durationDays = durationDays,
                durationInMinutes = durationInMinutes,
                onDurationDaysChange = { new ->
                    durationDays = new
                    builder.setDurationDays(new)
                    builder.setDurationInMinutes(null)
                    updateChanges()
                },
                onDurationInMinutesChange = { new ->
                    durationInMinutes = new
                    builder.setDurationInMinutes(new)
                    builder.setDurationDays(null)
                    updateChanges()
                },
                onModeChange = { newMode ->
                    durationMode = newMode
                    when (newMode) {
                        DurationMode.DAYS -> {
                            builder.setDurationDays(durationDays)
                            builder.setDurationInMinutes(null)
                        }
                        DurationMode.MINUTES -> {
                            builder.setDurationInMinutes(durationInMinutes)
                            builder.setDurationDays(null)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName,
            )

            TimeOfDaySection(
                selectedValue = startHourOfDay,
                onValueChange = { newTime ->
                    startHourOfDay = newTime
                    builder.setStartHourOfDay(newTime)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasDescription && (durationMode == DurationMode.MINUTES),
                title = "Kiedy zacząć?"
            )

        }
    }
}

// Duration mode components for persistent series
enum class DurationMode {
    DAYS, MINUTES;
}

fun durationModeOption(
    mode: DurationMode,
    durationDays: Int,
    durationInMinutes: Int,
    inputMode: PlanCreatorInputMode,
    onDurationDaysChange: (Int) -> Unit,
    onDurationInMinutesChange: (Int) -> Unit,
    onModeChange: (DurationMode) -> Unit = {},
): ModeOption<DurationMode> = when (mode) {
    DurationMode.DAYS -> ModeOption(
        onClick = { onModeChange(DurationMode.DAYS) }
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText("Przez")
            PlanCreatorIntegerInput(
                value = durationDays,
                onValueChange = onDurationDaysChange,
                mode = inputMode,
                textStyle = TextStyles.medium16,
                minValue = 1
            )
            SpanText(if (durationDays == 1) "dzień" else "dni")
        }
    }
    DurationMode.MINUTES -> ModeOption(
        onClick = { onModeChange(DurationMode.MINUTES) }
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText("Przez")
            PlanCreatorIntegerInput(
                value = durationInMinutes,
                onValueChange = onDurationInMinutesChange,
                mode = inputMode,
                textStyle = TextStyles.medium16,
                minValue = 1
            )
            SpanText("minut")
        }
    }
}

@Composable
internal fun DurationPersistentInput(
    selectedValue: DurationMode,
    durationDays: Int,
    durationInMinutes: Int,
    onDurationDaysChange: (Int) -> Unit,
    onDurationInMinutesChange: (Int) -> Unit,
    onModeChange: (DurationMode) -> Unit = {},
    mode: PlanCreatorInputMode,
    enabled: Boolean = true,
) = EditViewSection(
    enabled = enabled,
    title = "Jak długo trwa?",
    withTitle = false,
) {
    ModeSelectorInput(
        options = listOf(DurationMode.DAYS, DurationMode.MINUTES),
        optionToMode = { durationMode ->
            durationModeOption(
                mode = durationMode,
                durationDays = durationDays,
                durationInMinutes = durationInMinutes,
                inputMode = mode,
                onDurationDaysChange = onDurationDaysChange,
                onDurationInMinutesChange = onDurationInMinutesChange,
                onModeChange = onModeChange,
            )
        },
        selectedValue = selectedValue,
        mode = mode,
        enabled = enabled,
    )
}